import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Input,
  Button,
  Space,
  Tag,
  Typography,
  Card,
  message,
  Select,
  DatePicker,
  Row,
  Col,
  Alert,
  Avatar,
  Tabs,
  Tooltip,
  Popconfirm,
  Descriptions,
  Modal,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  DollarOutlined,
  ShopOutlined,
  GlobalOutlined,
  EyeOutlined,
  SafetyCertificateOutlined,
  SendOutlined,
  ExportOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
  accentDim: 'rgba(34, 211, 238, 0.1)',
};

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

/**
 * Pipeline states a Moralis deposit can be in. Kept in sync with
 * MORALIS_INCOMING_PROCESSING_STATUS in AdminMerchantApi.
 */
const PROCESSING_STATUS_META: Record<
  string,
  { label: string; color: string; help: string }
> = {
  RECEIVED: {
    label: 'Received',
    color: 'default',
    help: 'Stored from the Moralis webhook. Not yet actionable - usually still waiting for the confirmed copy of the event.',
  },
  VERIFIED: {
    label: 'Verified',
    color: 'cyan',
    help: 'Transaction hash and amount were independently confirmed on chain. Ready to be posted to the sweep list.',
  },
  SWEEP_CREATED: {
    label: 'Swept',
    color: 'green',
    help: 'A sweep remittance entry exists for this deposit and the merchant was notified.',
  },
  OUTGOING_TRANSFER: {
    label: 'Outgoing / remittance',
    color: 'blue',
    help: 'Money leaving one of our wallets — typically a sweep from a child wallet to the merchant custodial address. Moralis reports these because a watched address was the sender. Recorded for visibility only: never swept, never sent to the merchant as a deposit.',
  },
  GAS_TOPUP: {
    label: 'Gas top-up',
    color: 'geekblue',
    help: 'Our gas wallet funding this wallet so it can pay for its own sweep. It lands on a wallet we manage, but the money is ours and no customer deposited it — so it is tagged and parked: never swept, never sent to the merchant.',
  },
  WALLET_NOT_MATCHED: {
    label: 'Not our wallet',
    color: 'orange',
    help: 'Neither the recipient nor the sender belongs to any wallet we manage.',
  },
  UNSUPPORTED_CONTRACT: {
    label: 'Unsupported token',
    color: 'gold',
    help: 'The token contract is not one of our supported contract addresses on that chain.',
  },
  UNSUPPORTED_NETWORK: {
    label: 'Unsupported chain',
    color: 'gold',
    help: 'The chain the deposit came from is not an active blockchain network for us.',
  },
  VERIFICATION_FAILED: {
    label: 'Verification failed',
    color: 'red',
    help: 'The chain contradicted Moralis: transaction missing, reverted, or the amount did not match.',
  },
  GAS_WALLET_SKIPPED: {
    label: 'Gas wallet',
    color: 'purple',
    help: 'Deposit landed on a gas wallet - deliberately never swept.',
  },
  FAILED: {
    label: 'Failed',
    color: 'red',
    help: 'Something went wrong while processing. See the status reason.',
  },
};

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};

  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }

  @media (max-width: 768px) {
    margin-bottom: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;

    h2 {
      font-size: 20px !important;
    }
  }
`;

const FiltersContainer = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;

  .ant-card-body {
    padding: 20px !important;
  }

  @media (max-width: 768px) {
    margin-bottom: 16px;

    .ant-card-body {
      padding: 12px !important;
    }
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }

  /* Ant Design's pagination assumes a light background, so page numbers and the
     page-size dropdown are invisible on this dark theme unless forced. */
  .ant-pagination {
    .ant-pagination-item {
      background: ${COLORS.bgPrimary};
      border-color: ${COLORS.border};

      a {
        color: ${COLORS.textPrimary};
      }

      &:hover {
        border-color: ${COLORS.accent};

        a {
          color: ${COLORS.accent};
        }
      }
    }

    .ant-pagination-item-active {
      background: ${COLORS.accentDim};
      border-color: ${COLORS.accent};

      a {
        color: ${COLORS.accent};
      }
    }

    .ant-pagination-prev,
    .ant-pagination-next {
      .ant-pagination-item-link {
        background: ${COLORS.bgPrimary};
        border-color: ${COLORS.border};
        color: ${COLORS.textPrimary};
      }

      &:hover .ant-pagination-item-link {
        border-color: ${COLORS.accent};
        color: ${COLORS.accent};
      }

      &.ant-pagination-disabled .ant-pagination-item-link {
        color: ${COLORS.textSecondary};
        opacity: 0.5;
      }
    }

    .ant-pagination-options {
      .ant-select-selector {
        background: ${COLORS.bgPrimary} !important;
        border-color: ${COLORS.border} !important;
        color: ${COLORS.textPrimary} !important;
      }

      .ant-select-arrow {
        color: ${COLORS.textSecondary};
      }
    }
  }
`;

const StatusTabs = styled(Tabs)`
  margin-bottom: 16px;

  .ant-tabs-nav::before {
    border-bottom: 1px solid ${COLORS.border};
  }

  .ant-tabs-tab {
    .ant-tabs-tab-btn {
      color: ${COLORS.textSecondary};
    }

    &:hover .ant-tabs-tab-btn {
      color: ${COLORS.textPrimary};
    }
  }

  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${COLORS.accent} !important;
  }

  .ant-tabs-ink-bar {
    background: ${COLORS.accent};
  }
`;

const FilterRow = styled(Row)`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterLabel = styled.span`
  color: ${COLORS.textSecondary};
  margin-right: 8px;
  display: inline-block;
  min-width: 120px;
`;

const DescriptionCard = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;

  .ant-card-body {
    padding: 20px !important;
  }

  .ant-alert {
    background: ${COLORS.bgPrimary} !important;
    border: 1px solid ${COLORS.border} !important;

    .ant-alert-message {
      color: ${COLORS.textPrimary} !important;
    }

    .ant-alert-description {
      color: ${COLORS.textPrimary} !important;
    }

    .ant-alert-icon {
      color: ${COLORS.accent} !important;
    }
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-content,
  .ant-modal-header,
  .ant-modal-body {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
  }

  .ant-modal-header {
    border-bottom: 1px solid ${COLORS.border} !important;

    .ant-modal-title {
      color: ${COLORS.textPrimary} !important;
    }
  }

  .ant-modal-close {
    color: ${COLORS.textSecondary} !important;

    &:hover {
      color: ${COLORS.textPrimary} !important;
    }
  }

  .ant-descriptions-item-label {
    color: ${COLORS.textSecondary} !important;
    background: ${COLORS.bgPrimary} !important;
  }

  .ant-descriptions-item-content {
    color: ${COLORS.textPrimary} !important;
    background: ${COLORS.bgSecondary} !important;
    word-break: break-all;
  }

  .ant-descriptions-bordered .ant-descriptions-view {
    border-color: ${COLORS.border} !important;
  }

  .ant-descriptions-row {
    border-color: ${COLORS.border} !important;
  }
`;

const MoralisIncomingTransactions: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [deposits, setDeposits] = useState<any[]>([]);
  const [merchants, setMerchants] = useState<any[]>([]);
  const [chains, setChains] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({ ALL: 0 });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const [actioningId, setActioningId] = useState<string | null>(null);
  const [deletingAll, setDeletingAll] = useState(false);
  const [detailsRecord, setDetailsRecord] = useState<any>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const merchantFilter = searchParams.get('merchantCompanyId') || '';
  const chainFilter = searchParams.get('blockchainNetworkId') || '';
  const currencyFilter = searchParams.get('currencyId') || '';
  const transferTypeFilter = searchParams.get('transferType') || '';
  // Defaults to DEPOSITS: the page should open on money that actually concerns us,
  // not on our own gas top-ups, remittances and unsupported transfers.
  const statusFilter = searchParams.get('processingStatus') || 'DEPOSITS';
  const dateFrom = searchParams.get('dateFrom');
  const dateTo = searchParams.get('dateTo');
  // Typed as any to match the antd/dayjs pairing used across the other admin pages -
  // this antd build ships Moment-typed pickers.
  const [dateRange, setDateRange] = useState<any>(
    dateFrom && dateTo ? [dayjs(dateFrom), dayjs(dateTo)] : null,
  );

  const pageId = parseInt(searchParams.get('pageId') || '1');
  const perPage = parseInt(searchParams.get('perPage') || '10');

  const buildSharedFilterParams = useCallback(() => {
    const params: any = {};
    if (searchParams.get('search')) params.search = searchParams.get('search');
    if (merchantFilter) params.merchantCompanyId = merchantFilter;
    if (chainFilter) params.blockchainNetworkId = chainFilter;
    if (currencyFilter) params.currencyId = currencyFilter;
    if (transferTypeFilter) params.transferType = transferTypeFilter;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    return params;
  }, [searchParams, merchantFilter, chainFilter, currencyFilter, transferTypeFilter, dateFrom, dateTo]);

  const fetchDeposits = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        pageId,
        perPage,
        sort: 'DESC',
        ...buildSharedFilterParams(),
      };
      if (statusFilter && statusFilter !== 'ALL') {
        params.processingStatus = statusFilter;
      }

      const response = await apiClient.get(
        API_ENDPOINTS.ADMIN_GET_MORALIS_INCOMING_TRANSACTIONS,
        { params },
      );

      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        setDeposits(data.itemsList || []);
        const paginator = data.paginator;
        if (paginator) {
          setPagination({
            current: paginator.currentPage || pageId,
            pageSize: paginator.perPage || perPage,
            total: paginator.itemCount || 0,
          });
        }
      }
    } catch (error: any) {
      console.error('Error fetching Moralis incoming transactions:', error);
      message.error(
        error.response?.data?.message || 'Failed to fetch incoming deposits',
      );
    } finally {
      setLoading(false);
    }
  }, [pageId, perPage, statusFilter, buildSharedFilterParams]);

  const fetchStatusCounts = useCallback(async () => {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.ADMIN_GET_MORALIS_INCOMING_STATS,
        { params: buildSharedFilterParams() },
      );
      if (response.data?.status === 'success' && response.data?.data) {
        setStatusCounts(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching incoming deposit stats:', error);
    }
  }, [buildSharedFilterParams]);

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const [merchantsRes, chainsRes, currenciesRes] = await Promise.all([
          apiClient.get(API_ENDPOINTS.ADMIN_GET_WALLET_MERCHANTS),
          apiClient.get(API_ENDPOINTS.ADMIN_GET_BLOCKCHAIN_NETWORKS, {
            params: { pageId: 1, perPage: 100, status: 'ACTIVE' },
          }),
          apiClient.get('/api/Administrator/currencies', {
            params: { pageId: 1, perPage: 100, status: 'ACTIVE' },
          }),
        ]);

        if (merchantsRes.data?.status === 'success') {
          setMerchants(merchantsRes.data.data || []);
        }
        if (chainsRes.data?.status === 'success') {
          const data = chainsRes.data.data;
          setChains(data?.itemsList || data?.items || (Array.isArray(data) ? data : []));
        }
        if (currenciesRes.data?.status === 'success') {
          setCurrencies(currenciesRes.data.data?.itemsList || []);
        }
      } catch (error) {
        console.error('Error loading filter options:', error);
      }
    };

    loadFilterOptions();
  }, []);

  useEffect(() => {
    fetchDeposits();
  }, [fetchDeposits]);

  useEffect(() => {
    fetchStatusCounts();
  }, [fetchStatusCounts]);

  const updateURLParams = (updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    newParams.set('pageId', '1');
    setSearchParams(newParams);
  };

  const handleTableChange = (newPagination: any) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pageId', newPagination.current.toString());
    newParams.set('perPage', newPagination.pageSize.toString());
    setSearchParams(newParams);
  };

  const handleDateRangeChange = (dates: any) => {
    setDateRange(dates);
    updateURLParams({
      dateFrom: dates && dates[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : null,
      dateTo: dates && dates[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : null,
    });
  };

  const handleClearFilters = () => {
    setSearch('');
    setDateRange(null);
    // Empty params means the default view, which is DEPOSITS.
    setSearchParams(new URLSearchParams());
  };

  const refresh = () => {
    fetchDeposits();
    fetchStatusCounts();
  };

  /** Runs one of the per-row admin actions and refreshes the list afterwards. */
  const runAction = async (
    record: any,
    endpoint: string,
    successMessage: string,
  ) => {
    try {
      setActioningId(record.id);
      const response = await apiClient.post(endpoint.replace('{id}', record.id));
      message.success(response.data?.message || successMessage);
      refresh();
    } catch (error: any) {
      message.error(
        error.response?.data?.message || `Failed to ${successMessage.toLowerCase()}`,
      );
    } finally {
      setActioningId(null);
    }
  };

  const handleDelete = async (record: any) => {
    try {
      setActioningId(record.id);
      await apiClient.delete(
        API_ENDPOINTS.ADMIN_DELETE_MORALIS_INCOMING_TRANSACTION.replace(
          '{id}',
          record.id,
        ),
      );
      message.success('Deposit record deleted');
      refresh();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete record');
    } finally {
      setActioningId(null);
    }
  };

  /**
   * Deletes everything matching the filter currently on screen. The server rebuilds the
   * same where-clause the list uses, so what is deleted is exactly what is listed -
   * minus anything that produced a sweep, which it refuses to touch.
   */
  const deleteAllFiltered = async () => {
    try {
      setDeletingAll(true);
      const response = await apiClient.post(
        API_ENDPOINTS.ADMIN_DELETE_ALL_MORALIS_INCOMING,
        {
          ...buildSharedFilterParams(),
          ...(statusFilter && statusFilter !== 'ALL'
            ? { processingStatus: statusFilter }
            : {}),
        },
      );
      message.success(response.data?.message || 'Deposits deleted');
      refresh();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete deposits');
    } finally {
      setDeletingAll(false);
    }
  };

  const openDetails = async (record: any) => {
    try {
      setDetailsLoading(true);
      setDetailsRecord(record);
      const response = await apiClient.get(
        API_ENDPOINTS.ADMIN_GET_MORALIS_INCOMING_TRANSACTION.replace(
          '{id}',
          record.id,
        ),
      );
      if (response.data?.status === 'success') {
        setDetailsRecord(response.data.data);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load deposit details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const explorerTxUrl = (record: any) => {
    const base = record?.blockchainNetwork?.explorerUrl;
    if (!base || !record?.transactionHash) return null;
    return `${base.replace(/\/$/, '')}/tx/${record.transactionHash}`;
  };

  const shortenHash = (value?: string | null, head = 10, tail = 8) => {
    if (!value) return '-';
    if (value.length <= head + tail) return value;
    return `${value.substring(0, head)}...${value.substring(value.length - tail)}`;
  };

  const columns = [
    {
      title: 'Status',
      dataIndex: 'processingStatus',
      key: 'processingStatus',
      render: (status: string, record: any) => {
        const meta = PROCESSING_STATUS_META[status] || {
          label: status,
          color: 'default',
          help: '',
        };
        return (
          <Tooltip title={record.statusReason || meta.help}>
            <Tag color={meta.color}>{meta.label}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Currency',
      dataIndex: ['currency', 'symbol'],
      key: 'currency',
      render: (symbol: string, record: any) => (
        <Space>
          <Avatar
            src={record.currency?.iconUrl}
            icon={<DollarOutlined />}
            size={20}
            style={{
              backgroundColor: COLORS.bgSecondary,
              color: COLORS.textPrimary,
              border: `1px solid ${COLORS.border}`,
            }}
          />
          <span>{symbol || record.tokenSymbol || '-'}</span>
        </Space>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: string, record: any) => (
        <span>
          {amount != null
            ? Number(amount).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 8,
              })
            : '-'}{' '}
          {record.currencySymbol || record.tokenSymbol || ''}
        </span>
      ),
    },
    {
      title: 'Chain',
      dataIndex: ['blockchainNetwork', 'name'],
      key: 'chain',
      render: (name: string, record: any) => (
        <Space>
          <Avatar
            src={record.blockchainNetwork?.iconUrl}
            icon={<GlobalOutlined />}
            size={20}
            style={{
              backgroundColor: COLORS.bgSecondary,
              color: COLORS.textPrimary,
              border: `1px solid ${COLORS.border}`,
            }}
          />
          <span>{name || record.chainIdHex || '-'}</span>
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'transferType',
      key: 'transferType',
      render: (type: string) => (
        <Tag color={type === 'ERC20' ? 'blue' : 'geekblue'}>{type}</Tag>
      ),
    },
    {
      title: 'Merchant',
      dataIndex: ['merchantCompany', 'name'],
      key: 'merchant',
      render: (name: string, record: any) => {
        if (!record.merchantCompany?.id) {
          return <Text style={{ color: COLORS.textSecondary }}>-</Text>;
        }
        return (
          <Space
            style={{ cursor: 'pointer' }}
            onClick={() => navigate(`/kivu/companies/${record.merchantCompany.id}`)}
          >
            <Avatar
              src={record.merchantCompany?.logoUrl}
              icon={<ShopOutlined />}
              size={24}
              style={{
                backgroundColor: COLORS.bgSecondary,
                color: COLORS.textPrimary,
                border: `1px solid ${COLORS.border}`,
              }}
            />
            <span style={{ color: COLORS.accent }}>{name}</span>
          </Space>
        );
      },
    },
    {
      title: 'Wallet',
      dataIndex: 'walletId',
      key: 'walletId',
      render: (walletId: string, record: any) => {
        if (!walletId) {
          return (
            <Tooltip title={record.toAddress}>
              <span style={{ fontFamily: 'monospace', fontSize: '12px', color: COLORS.textSecondary }}>
                {shortenHash(record.toAddress)}
              </span>
            </Tooltip>
          );
        }
        return (
          <Tooltip title={`${walletId}\n${record.toAddress}`}>
            <span
              style={{
                fontFamily: 'monospace',
                fontSize: '12px',
                cursor: record.wallet?.id ? 'pointer' : 'default',
                color: record.wallet?.id ? COLORS.accent : COLORS.textPrimary,
              }}
              onClick={() => record.wallet?.id && navigate(`/kivu/wallets/${record.wallet.id}`)}
            >
              {shortenHash(walletId, 14, 4)}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Transaction',
      dataIndex: 'transactionHash',
      key: 'transactionHash',
      render: (hash: string, record: any) => {
        const url = explorerTxUrl(record);
        return (
          <Space size={4}>
            <Tooltip title={hash}>
              <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                {shortenHash(hash)}
              </span>
            </Tooltip>
            {url && (
              <a href={url} target="_blank" rel="noopener noreferrer">
                <ExportOutlined style={{ color: COLORS.accent }} />
              </a>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Verified',
      dataIndex: 'verified',
      key: 'verified',
      render: (verified: boolean, record: any) => (
        <Tooltip
          title={
            verified
              ? `Confirmed on chain${record.confirmations ? ` (${record.confirmations} confirmations)` : ''}`
              : record.verificationError ||
                (record.confirmed
                  ? 'Not verified on chain yet'
                  : 'Waiting for Moralis to confirm the event')
          }
        >
          <Tag color={verified ? 'green' : record.confirmed ? 'orange' : 'default'}>
            {verified ? 'On chain' : record.confirmed ? 'Unverified' : 'Pending'}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Merchant notified',
      dataIndex: 'webhookSource',
      key: 'webhookSource',
      render: (source: string, record: any) => {
        if (!record.webhookLogId) {
          return (
            <Tooltip title="The merchant has not been told about this deposit yet.">
              <Tag>Not sent</Tag>
            </Tooltip>
          );
        }
        if (source === 'WATCHER') {
          return (
            <Tooltip title="OnchainWatcher discovered this transaction first and reported it to the merchant directly. We linked to its webhook log instead of sending a duplicate.">
              <Tag color="purple">By watcher</Tag>
            </Tooltip>
          );
        }
        return (
          <Tooltip
            title={
              record.webhookSentAt
                ? `Sent by us ${dayjs(record.webhookSentAt).format('YYYY-MM-DD HH:mm:ss')}`
                : 'Sent by us'
            }
          >
            <Tag color="cyan">By Moralis</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Received',
      dataIndex: 'receivedAt',
      key: 'receivedAt',
      render: (receivedAt: string) =>
        receivedAt ? (
          <Tooltip title={dayjs(receivedAt).format('YYYY-MM-DD HH:mm:ss')}>
            <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>
              {dayjs(receivedAt).fromNow()}
            </span>
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => {
        const canPostToSweep =
          record.verified && !record.sweepListId && record.merchantCompanyId;
        // The webhook action doubles as a resend: a deposit the watcher already
        // reported, or one whose delivery failed, can still be pushed to the merchant.
        const canSendWebhook = record.verified && record.merchantCompanyId;
        const alreadyNotified = !!record.webhookLogId;

        return (
          <Space size={4} wrap>
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => openDetails(record)}
              style={{ color: COLORS.accent, padding: 0 }}
            >
              Details
            </Button>

            {!record.verified && record.confirmed && (
              <Button
                type="link"
                icon={<SafetyCertificateOutlined />}
                loading={actioningId === record.id}
                onClick={() =>
                  runAction(
                    record,
                    API_ENDPOINTS.ADMIN_VERIFY_MORALIS_INCOMING_TRANSACTION,
                    'Verify deposit',
                  )
                }
                style={{ color: COLORS.accent, padding: 0 }}
              >
                Verify
              </Button>
            )}

            {canPostToSweep && (
              <Popconfirm
                title="Post this deposit to the sweep list? A PENDING sweep remittance will be created and the merchant notified."
                onConfirm={() =>
                  runAction(
                    record,
                    API_ENDPOINTS.ADMIN_POST_MORALIS_INCOMING_TO_SWEEP,
                    'Post to sweep list',
                  )
                }
                okText="Post"
                cancelText="Cancel"
              >
                <Button
                  type="link"
                  loading={actioningId === record.id}
                  style={{ color: COLORS.accent, padding: 0 }}
                >
                  Post to sweep
                </Button>
              </Popconfirm>
            )}

            {record.sweepListId && (
              <Button
                type="link"
                onClick={() => navigate(`/kivu/sweep-list/${record.sweepListId}`)}
                style={{ color: COLORS.accent, padding: 0 }}
              >
                View sweep
              </Button>
            )}

            {canSendWebhook && (
              <Popconfirm
                title={
                  alreadyNotified
                    ? 'Re-send the TRANSACTION_DISCOVERED webhook to this merchant? It will be re-signed and posted to their current webhook URL.'
                    : 'Send the TRANSACTION_DISCOVERED webhook to this merchant now?'
                }
                onConfirm={() =>
                  runAction(
                    record,
                    API_ENDPOINTS.ADMIN_SEND_MORALIS_INCOMING_WEBHOOK,
                    alreadyNotified ? 'Resend merchant webhook' : 'Send merchant webhook',
                  )
                }
                okText={alreadyNotified ? 'Resend' : 'Send'}
                cancelText="Cancel"
              >
                <Button
                  type="link"
                  icon={<SendOutlined />}
                  loading={actioningId === record.id}
                  style={{ color: COLORS.accent, padding: 0 }}
                >
                  {alreadyNotified ? 'Resend webhook' : 'Send webhook'}
                </Button>
              </Popconfirm>
            )}

            <Popconfirm
              title="Delete this deposit record? It is hidden from the list but kept for audit."
              onConfirm={() => handleDelete(record)}
              okText="Delete"
              cancelText="Cancel"
            >
              <Button
                type="link"
                icon={<DeleteOutlined />}
                loading={actioningId === record.id}
                danger
                style={{ padding: 0 }}
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const statusTabItems = [
    {
      key: 'DEPOSITS',
      label: (
        <Tooltip title="Real deposits into child wallets. Excludes gas top-ups, outgoing remittances, deposits to gas wallets, unsupported tokens/chains, and addresses that are not ours.">
          <span>
            Deposits{' '}
            <Tag color="green" style={{ marginLeft: 4 }}>
              {statusCounts.DEPOSITS ?? 0}
            </Tag>
          </span>
        </Tooltip>
      ),
    },
    {
      key: 'ALL',
      label: (
        <Tooltip title="Everything Moralis reported, including transfers that are not deposits.">
          <span>
            All <Tag style={{ marginLeft: 4 }}>{statusCounts.ALL ?? 0}</Tag>
          </span>
        </Tooltip>
      ),
    },
    ...Object.entries(PROCESSING_STATUS_META).map(([key, meta]) => ({
      key,
      label: (
        <span>
          {meta.label}{' '}
          <Tag color={meta.color} style={{ marginLeft: 4 }}>
            {statusCounts[key] ?? 0}
          </Tag>
        </span>
      ),
    })),
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
          Incoming Deposits (Moralis)
        </Title>
        <Space>
          <Popconfirm
            title={
              <div style={{ maxWidth: 320 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  Delete these deposits?
                </div>
                <div style={{ fontWeight: 400 }}>
                  <strong>{pagination.total}</strong> record(s) match the current filter
                  {statusFilter && statusFilter !== 'ALL'
                    ? ` (${PROCESSING_STATUS_META[statusFilter]?.label || statusFilter})`
                    : ''}
                  .
                </div>
                <div style={{ marginTop: 6, fontWeight: 400, color: COLORS.textSecondary }}>
                  Anything that produced a sweep is kept, so the audit trail behind
                  moved money stays intact. This is a soft delete — rows remain in the
                  database.
                </div>
              </div>
            }
            onConfirm={deleteAllFiltered}
            okText={`Delete ${pagination.total}`}
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            disabled={!pagination.total}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={deletingAll}
              disabled={!pagination.total}
            >
              Delete all ({pagination.total})
            </Button>
          </Popconfirm>
          <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>
            Refresh
          </Button>
        </Space>
      </HeaderContainer>

      <DescriptionCard>
        <Alert
          message="About Incoming Deposits"
          description={
            <div>
              <Text style={{ color: COLORS.textPrimary }}>
                Every deposit Moralis Streams reports to <code>/Hook/MoralisStreamerRobot</code> is
                recorded here, whether or not it concerns us. Nothing Moralis says is trusted on its
                own: each deposit is matched against our wallets, networks and supported contracts,
                then the transaction hash and the amount are independently confirmed on chain
                through our own RPC endpoints. Only a <strong>Verified</strong> deposit can become a
                sweep remittance or trigger a merchant webhook — use the row actions to push one
                through manually when automatic processing is off or an earlier attempt failed.
              </Text>
            </div>
          }
          type="info"
          icon={<InfoCircleOutlined />}
          showIcon
          style={{ background: COLORS.bgPrimary, border: `1px solid ${COLORS.border}` }}
        />
      </DescriptionCard>

      <FiltersContainer>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <FilterRow gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Search</FilterLabel>
              <Input
                placeholder="Tx hash, address, wallet ID, token"
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onPressEnter={() => updateURLParams({ search: search || null })}
                onBlur={() => updateURLParams({ search: search || null })}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Merchant</FilterLabel>
              <Select
                placeholder="All merchants"
                value={merchantFilter || undefined}
                onChange={(value) => updateURLParams({ merchantCompanyId: value || null })}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (String(option?.label) ?? '').toLowerCase().includes(input.toLowerCase())
                }
                style={{ width: '100%' }}
                options={merchants.map((m) => ({ value: m.id, label: m.name }))}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Chain</FilterLabel>
              <Select
                placeholder="All chains"
                value={chainFilter || undefined}
                onChange={(value) => updateURLParams({ blockchainNetworkId: value || null })}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (String(option?.label) ?? '').toLowerCase().includes(input.toLowerCase())
                }
                style={{ width: '100%' }}
                options={chains.map((c) => ({ value: c.id, label: c.name }))}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Currency</FilterLabel>
              <Select
                placeholder="All currencies"
                value={currencyFilter || undefined}
                onChange={(value) => updateURLParams({ currencyId: value || null })}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (String(option?.label) ?? '').toLowerCase().includes(input.toLowerCase())
                }
                style={{ width: '100%' }}
                options={currencies.map((c) => ({ value: c.id, label: c.symbol }))}
              />
            </Col>
          </FilterRow>
          <FilterRow gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>Transfer type</FilterLabel>
              <Select
                placeholder="All types"
                value={transferTypeFilter || undefined}
                onChange={(value) => updateURLParams({ transferType: value || null })}
                allowClear
                style={{ width: '100%' }}
                options={[
                  { value: 'ERC20', label: 'ERC20 token' },
                  { value: 'NATIVE', label: 'Native coin' },
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <FilterLabel>Received between</FilterLabel>
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <FilterLabel>&nbsp;</FilterLabel>
              <Button onClick={handleClearFilters} style={{ width: '100%' }}>
                Clear filters
              </Button>
            </Col>
          </FilterRow>
        </Space>
      </FiltersContainer>

      <Card
        style={{
          background: COLORS.bgSecondary,
          border: `1px solid ${COLORS.border}`,
        }}
        bodyStyle={{ padding: 16 }}
      >
        <StatusTabs
          activeKey={statusFilter}
          onChange={(key) => updateURLParams({ processingStatus: key })}
          items={statusTabItems}
        />
        <TableScrollWrapper>
          <Table
            columns={columns}
            dataSource={deposits}
            rowKey="id"
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: window.innerWidth > 768,
              showTotal: (total, range) => (
                <span style={{ color: COLORS.textSecondary }}>
                  Showing {range[0]}-{range[1]} of {total} deposits
                </span>
              ),
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            style={{ background: 'transparent' }}
            size="small"
          />
        </TableScrollWrapper>
      </Card>

      <StyledModal
        title={detailsLoading ? 'Loading deposit details…' : 'Incoming Deposit Details'}
        open={!!detailsRecord}
        onCancel={() => setDetailsRecord(null)}
        footer={null}
        width={860}
      >
        {detailsRecord && (
          <Descriptions
            bordered
            size="small"
            column={{ xs: 1, sm: 1, md: 2 }}
          >
            <Descriptions.Item label="Status">
              <Tag color={PROCESSING_STATUS_META[detailsRecord.processingStatus]?.color}>
                {PROCESSING_STATUS_META[detailsRecord.processingStatus]?.label ||
                  detailsRecord.processingStatus}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Status reason" span={2}>
              {detailsRecord.statusReason || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Transaction hash" span={2}>
              {detailsRecord.transactionHash}
              {explorerTxUrl(detailsRecord) && (
                <a
                  href={explorerTxUrl(detailsRecord) as string}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ marginLeft: 8, color: COLORS.accent }}
                >
                  View on explorer
                </a>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Transfer type">
              {detailsRecord.transferType}
            </Descriptions.Item>
            <Descriptions.Item label="Log index">
              {detailsRecord.logIndex ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="From">{detailsRecord.fromAddress || '-'}</Descriptions.Item>
            <Descriptions.Item label="To">{detailsRecord.toAddress}</Descriptions.Item>
            <Descriptions.Item label="Token contract" span={2}>
              {detailsRecord.contractAddress || 'Native coin'}
            </Descriptions.Item>
            <Descriptions.Item label="Amount">
              {detailsRecord.amount}{' '}
              {detailsRecord.currencySymbol || detailsRecord.tokenSymbol || ''}
            </Descriptions.Item>
            <Descriptions.Item label="Raw value">{detailsRecord.rawValue}</Descriptions.Item>
            <Descriptions.Item label="On-chain raw value">
              {detailsRecord.onChainRawValue || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Amount matched">
              <Tag color={detailsRecord.amountMatched ? 'green' : 'red'}>
                {detailsRecord.amountMatched ? 'Yes' : 'No'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Token (as reported)">
              {detailsRecord.tokenName || '-'} ({detailsRecord.tokenSymbol || '-'}),{' '}
              {detailsRecord.tokenDecimals ?? '-'} decimals
            </Descriptions.Item>
            <Descriptions.Item label="Possible spam">
              <Tag color={detailsRecord.possibleSpam ? 'red' : 'default'}>
                {detailsRecord.possibleSpam ? 'Yes' : 'No'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Chain">
              {detailsRecord.blockchainNetwork?.name || detailsRecord.chainIdHex || '-'} (
              {detailsRecord.networkId ?? '-'})
            </Descriptions.Item>
            <Descriptions.Item label="Block">
              {detailsRecord.blockNumber ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Block timestamp">
              {detailsRecord.blockTimestamp
                ? dayjs(detailsRecord.blockTimestamp).format('YYYY-MM-DD HH:mm:ss')
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Confirmations">
              {detailsRecord.confirmations ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Wallet">
              {detailsRecord.walletId || 'Not one of our wallets'}
            </Descriptions.Item>
            <Descriptions.Item label="Member">
              {detailsRecord.wallet?.externalMemberName ||
                detailsRecord.wallet?.externalMemberEmail ||
                '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Merchant">
              {detailsRecord.merchantCompany?.name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Verified">
              <Tag color={detailsRecord.verified ? 'green' : 'orange'}>
                {detailsRecord.verified ? 'Yes' : 'No'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Verification error" span={2}>
              {detailsRecord.verificationError || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Verification attempts">
              {detailsRecord.verificationAttempts ?? 0}
            </Descriptions.Item>
            <Descriptions.Item label="Moralis confirmed">
              <Tag color={detailsRecord.confirmed ? 'green' : 'default'}>
                {detailsRecord.confirmed ? 'Yes' : 'No'}
              </Tag>
            </Descriptions.Item>
            {detailsRecord.processingStatus === 'OUTGOING_TRANSFER' && (
              <Descriptions.Item label="Executed sweep" span={2}>
                {detailsRecord.outgoingSweepListId ? (
                  <Button
                    type="link"
                    style={{ color: COLORS.accent, padding: 0 }}
                    onClick={() =>
                      navigate(`/kivu/sweep-list/${detailsRecord.outgoingSweepListId}`)
                    }
                  >
                    Open the sweep this transaction paid out
                  </Button>
                ) : (
                  'No matching sweep found for this transaction hash'
                )}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Sweep remittance" span={2}>
              {detailsRecord.sweepListItem ? (
                <Space>
                  <Tag color="green">{detailsRecord.sweepListItem.sweepStatus}</Tag>
                  <Button
                    type="link"
                    style={{ color: COLORS.accent, padding: 0 }}
                    onClick={() => navigate(`/kivu/sweep-list/${detailsRecord.sweepListId}`)}
                  >
                    Open sweep
                  </Button>
                </Space>
              ) : (
                'Not posted to the sweep list'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Merchant webhook" span={2}>
              {detailsRecord.webhookLog ? (
                <Space wrap>
                  <Tag
                    color={
                      detailsRecord.webhookLog.deliveryStatus === 'SUCCESS'
                        ? 'green'
                        : detailsRecord.webhookLog.deliveryStatus === 'WATCHER'
                          ? 'purple'
                          : 'orange'
                    }
                  >
                    {detailsRecord.webhookLog.deliveryStatus}
                  </Tag>
                  <span>{detailsRecord.webhookLog.eventType}</span>
                  {detailsRecord.webhookLog.retryCount > 0 && (
                    <Text style={{ color: COLORS.textSecondary, fontSize: 12 }}>
                      {detailsRecord.webhookLog.retryCount} retries
                    </Text>
                  )}
                </Space>
              ) : (
                'Not sent'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Notified by" span={2}>
              {!detailsRecord.webhookLogId ? (
                'Nobody yet'
              ) : detailsRecord.webhookSource === 'WATCHER' ? (
                <Space>
                  <Tag color="purple">OnchainWatcher</Tag>
                  <Text style={{ color: COLORS.textSecondary }}>
                    discovered first and reported it directly — no duplicate sent
                  </Text>
                </Space>
              ) : (
                <Space>
                  <Tag color="cyan">Moralis pipeline</Tag>
                  {detailsRecord.webhookSentAt && (
                    <Text style={{ color: COLORS.textSecondary }}>
                      {dayjs(detailsRecord.webhookSentAt).format('YYYY-MM-DD HH:mm:ss')}
                    </Text>
                  )}
                </Space>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Stream">
              {detailsRecord.streamId || '-'}{' '}
              {detailsRecord.streamTag ? `(${detailsRecord.streamTag})` : ''}
            </Descriptions.Item>
            <Descriptions.Item label="Signature verified">
              <Tag color={detailsRecord.signatureValid ? 'green' : 'red'}>
                {detailsRecord.signatureValid ? 'Yes' : 'No'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Received at">
              {detailsRecord.receivedAt
                ? dayjs(detailsRecord.receivedAt).format('YYYY-MM-DD HH:mm:ss')
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Dedupe key" span={2}>
              <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                {detailsRecord.dedupeKey}
              </span>
            </Descriptions.Item>
          </Descriptions>
        )}
      </StyledModal>
    </PageContainer>
  );
};

export default MoralisIncomingTransactions;
